import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle, Target, Eye, Heart } from "lucide-react";
import "./AboutSection.css";

const AboutSection = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);
  return (
    <div className="about-wrapper">
      {/* Intro Section */}
      <section className="about-intro section-padding">
        <div className="container">
          <div className="about-grid-layout">
            <div className="about-text-content">
              <span className="section-badge">About Relax Infinity</span>
              <h2>Immerse yourself in tranquility</h2>
              <div className="about-description">
                <p>
                  Immerse yourself in tranquility with the Relax Infinity app,
                  offering both guided and unguided meditation routines designed
                  to elevate your mind and spirit. Relax is your sanctuary to
                  escape the daily hustle and find peace, even if only for a few
                  precious minutes each day.
                </p>
                <p>
                  Tailored to your unique needs, this app is more than just a
                  meditation tool—it’s an invitation to experience the art of
                  intermittent silence and deep awareness. As you incorporate
                  this practice into your life, you’ll notice a shift in your
                  perspective and how others perceive you.
                </p>

                <div className="highlight-box">
                  <p>
                    At Relax, we’re more than just an app — we’re your daily
                    companion for calmness, clarity, and better living.
                  </p>
                </div>
              </div>

              <div className="why-choose-list">
                <h3>Why Choose Relax?</h3>
                <ul>
                  <li>
                    <CheckCircle className="check-icon" />{" "}
                    <span>Easy-to-use and beautifully designed experience</span>
                  </li>
                  <li>
                    <CheckCircle className="check-icon" />{" "}
                    <span>Stress relief and mindfulness support</span>
                  </li>
                  <li>
                    <CheckCircle className="check-icon" />{" "}
                    <span>Sleep-enhancing sounds and calming sessions</span>
                  </li>
                  <li>
                    <CheckCircle className="check-icon" />{" "}
                    <span>Wellness tools available anytime, anywhere</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="about-visuals">
              <div className="about-image-wrapper">
                <img
                  src="/images/about-main.png"
                  alt="Relaxing with our wellness app"
                  className="about-main-image"
                />
                <div className="image-overlay"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="mvv-section section-padding">
        <div className="container">
          <div className="mvv-grid">
            <div className="mvv-card">
              <div className="mvv-icon">
                <Target size={32} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To create a healthier, harmonious and happier world through
                individual transformation.
              </p>
            </div>
            <div className="mvv-card">
              <div className="mvv-icon">
                <Eye size={32} />
              </div>
              <h3>Our Vision</h3>
              <p>To support everyone in personal growth and wellbeing.</p>
            </div>
            <div className="mvv-card">
              <div className="mvv-icon">
                <Heart size={32} />
              </div>
              <h3>Our Values</h3>
              <ul className="values-list">
                <li>Resilience</li>
                <li>Growth & Success</li>
                <li>Self-Discovery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-story-section section-padding">
        <div className="container">
          <div className="founder-layout">
            <div className="founder-image-col">
              <div className="founder-portrait-wrapper">
                <img
                  src="/images/krishna-bhatta.jpeg"
                  alt="Dr. Krishna Bhatta"
                  className="founder-portrait"
                />
                <div className="founder-name-plate">
                  <h4>Dr. Krishna Bhatta</h4>
                  <span>Founder & Visionary</span>
                </div>
              </div>
            </div>

            <div className="founder-text-col">
              <span className="section-badge">
                The Mind-Body Connection Expert
              </span>
              <h2>Meet Dr. Krishna Bhatta</h2>
              <p>
                Meet Dr. Krishna Bhatta, the visionary behind Relax Infinity.
                With expertise in both medicine and meditation, Dr. Bhatta is on
                a mission to revolutionize health and wellness. His meditation
                journey began even before his medical career, and he has since
                integrated mindfulness principles into his practice.
              </p>

              <div className="story-block">
                <h3>Silence, Mindfulness, and Meditation</h3>
                <p>
                  For Dr. Krishna Bhatta, achieving inner harmony means
                  embracing silence, mindfulness, and meditation. By combining
                  ancient wisdom with modern knowledge, he has uncovered the
                  transformative power of these practices. Grounded in the
                  physical world while exploring the depths of the inner self,
                  Dr. Bhatta emphasizes the importance of integrating silence
                  and mindfulness into daily life. It’s not about escaping
                  life’s challenges but building the resilience to navigate them
                  with grace.
                </p>
              </div>

              <div className="story-block">
                <h3>From Workshops to Worldwide Impact</h3>
                <p>
                  Driven by a desire to spread mindfulness and meditation
                  globally, Dr. Krishna Bhatta embarked on a journey that led to
                  the creation of the Relax Infinity app. Dissatisfied with
                  existing meditation apps, he aimed to fill the void by
                  offering a platform that delves deeper into the spiritual
                  journey. As the app continues to evolve and reach more people
                  worldwide, it serves as a catalyst for spiritual growth and
                  mindfulness.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* </div> */}
      </section>

      {/* Leadership Team Section */}
      <section className="leadership-section section-padding">
        <div className="container">
          <div className="leadership-header">
            <span className="section-badge">Leadership</span>
            <h2>Our Executive Partners</h2>
          </div>
          <div className="leadership-grid">
            <div className="leader-card">
              <div className="leader-image-wrapper">
                <img
                  src="/images/pramit.maakoday.jpg"
                  alt="Sarah Williams"
                  className="leader-image"
                />
              </div>
              <div className="leader-info">
                <h4>Pramit Maakoday</h4>
                <span className="leader-role">Co-Founder </span>
                <p>
                  Bringing operational excellence and a passion for mental
                  wellness, Pramit ensures Relax Infinity reaches its full
                  potential globally.
                </p>
              </div>
            </div>

            <div className="leader-card">
              <div className="leader-image-wrapper">
                <img
                  src="/images/chinmay-shah.png"
                  alt="Michael Chen"
                  className="leader-image"
                />
              </div>
              <div className="leader-info">
                <h4>Chinmay Shah</h4>
                <span className="leader-role">Co-Founder </span>
                <p>
                  With over a decade of experience in health-tech, Chinmay leads
                  the design of intuitive features that make our meditation
                  experiences seamless.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutSection;
